import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CreationMode } from '@components/storage-accounts/add-storage-account-wizard/add-storage-account-wizard.constants';
import { awsStorageType } from '@models/storage-accounts/storages.constants';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService, CanPipe } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  ButtonComponent,
  CardGroupComponent,
  ClipboardComponent,
  FormGroupComponent,
  InputComponent,
  InputLabelComponent,
  InputPasswordComponent,
  MbsSize,
  ModalService,
  SafePipe,
  SelectComponent,
  SwitcherComponent
} from 'mbs-ui-kit';
import { PermissionsEnum, StorageType } from 'mbs-ui-shared';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { HelpLinkPipe } from 'mbs-ui-shared@help/helpLink.pipe';
import { S3AuthType as AuthType } from 'mbs-ui-shared@models/StorageAccount';
import { e2eLocators, fakeSecretKey } from '../credential-forms.constants';
import { S3CredentialForm } from '../credential-forms.model';
import { MinimumRequiredPermissionsModalComponent } from './components/minimum-required-permissions-modal/minimum-required-permissions-modal.component';
import { getAuthTypeHints, getAuthTypes } from './credential-form-s3.constants';

@UntilDestroy()
@Component({
  selector: 'mbs-credential-form-s3',
  templateUrl: './credential-form-s3.component.html',
  styleUrls: ['../credential-forms.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    CardGroupComponent,
    ClipboardComponent,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    InputComponent,
    InputLabelComponent,
    InputPasswordComponent,
    NgbTooltipModule,
    ReactiveFormsModule,
    SafePipe,
    SelectComponent,
    SwitcherComponent,
    CanPipe,
    HelpLinkPipe
  ]
})
export class CredentialFormS3Component implements OnInit {
  private ability = inject(AbilityService);
  private authFacade = inject(AuthFacade);
  private helpService = inject(HelpService);
  private i18n = inject(I18NextPipe);
  private modalService = inject(ModalService);

  @Input() form: S3CredentialForm;
  @Input() mode: CreationMode;

  public readonly StorageType = StorageType;
  public readonly AuthType = AuthType;
  public readonly AuthTypeHints = getAuthTypeHints(this.ability);
  public readonly authTypes = getAuthTypes(this.i18n, this.helpService);
  public readonly mbsSize = MbsSize;
  public readonly PermissionEnum = PermissionsEnum;
  public readonly fakeSecretKey = fakeSecretKey;
  public readonly e2eLocators = e2eLocators;
  public readonly awsStorageType = awsStorageType(this.i18n);

  public storageTypeControl: FormControl;
  public arnRoleControl: FormControl;
  public isGovControl: FormControl;
  public authTypeControl: FormControl;
  public accessKeyControl: FormControl;
  public secretKeyControl: FormControl;

  ngOnInit() {
    this.defineControls();
    const defaultAuthType =
      this.mode === CreationMode.Create ? AuthType.MBS : this.arnRoleControl.value ? AuthType.Manual : AuthType.Legacy;

    this.storageTypeControl.valueChanges.pipe(untilDestroyed(this)).subscribe((type) => {
      this.patchAuthType(defaultAuthType, type);
    });

    this.authTypeControl.valueChanges.pipe(untilDestroyed(this)).subscribe((type) => {
      if (type === AuthType.MBS || type === AuthType.Legacy) {
        this.arnRoleControl.clearValidators();
        this.arnRoleControl.patchValue(null);
        this.accessKeyControl.setValidators([Validators.required]);
        this.secretKeyControl.setValidators([Validators.required]);
      }

      if (type === AuthType.Manual) {
        this.arnRoleControl.setValidators([Validators.required]);
        this.accessKeyControl.clearValidators();
        this.accessKeyControl.patchValue(null);
        this.secretKeyControl.clearValidators();
        this.secretKeyControl.patchValue(null);
      }
    });

    this.accessKeyControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.secretKeyControl.patchValue('');
      this.secretKeyControl.updateValueAndValidity();
    });

    this.patchAuthType(defaultAuthType, this.storageTypeControl.value);
  }

  public get id() {
    return this.authFacade.currentUserValue?.ProviderInfo.Id;
  }

  public get authType(): AuthType {
    return this.form.get('authType').value;
  }

  public get storageType(): StorageType {
    return this.form.get('storageType').value;
  }

  public openMinimumRequiredPermissionsModal() {
    this.modalService.openCustom(MinimumRequiredPermissionsModalComponent);
  }

  private patchAuthType(defaultAuthType: AuthType, type: StorageType) {
    if (type === StorageType.AmazonS3 || type === StorageType.AmazonS3Eu) {
      this.authTypeControl.patchValue(defaultAuthType);
    } else {
      this.authTypeControl.patchValue(AuthType.Manual);
    }
  }

  /* Define controls to prevent mistyping errors in templates */
  private defineControls() {
    this.storageTypeControl = this.form.get('storageType') as FormControl;
    this.arnRoleControl = this.form.get('arnRole') as FormControl;
    this.isGovControl = this.form.get('isGov') as FormControl;
    this.authTypeControl = this.form.get('authType') as FormControl;
    this.accessKeyControl = this.form.get('accessKey') as FormControl;
    this.secretKeyControl = this.form.get('secretKey') as FormControl;
  }
}
